/**
 * WebView Bridge Utility
 *
 * Provides helpers for communicating with the React Native WebView shell.
 * When the website is loaded inside a React Native WebView, the native app
 * listens for postMessage events. This module centralises that communication.
 *
 * Supported message types sent TO the native app:
 *   LOGOUT          – user-initiated logout
 *   TOKEN_INVALID   – API returned 401/403 (token no longer valid)
 *   SESSION_EXPIRED – session timed out (treated same as TOKEN_INVALID)
 *   OPEN_EXTERNAL_URL – open a URL in a native in-app browser with Done/back
 *   SET_ORIENTATION – temporarily lock the native shell portrait/landscape
 *   TEST_PERSON_CREATED – close an embedded Add Patient flow after success
 *   ADD_TEST_PERSON_CANCELLED – close an embedded Add Patient flow on cancel
 *   TEST_PERSON_UPDATED – close an embedded Edit Patient flow after a save
 *   EDIT_TEST_PERSON_CANCELLED – close an embedded Edit Patient flow on back
 *   (The four Add/Edit close events fire for cognitive customers only.)
 */

/**
 * Returns true when the page is rendered inside a React Native WebView.
 */
export const isWebView = () => {
  return typeof window !== "undefined" && !!window.ReactNativeWebView;
};
/**
 * Send a structured message to the React Native WebView host.
 *
 * @param {string} type    – One of LOGOUT | TOKEN_INVALID | SESSION_EXPIRED
 * @param {object} payload – Optional extra data
 * @returns {boolean} true if the message was sent (i.e. we are in a WebView)
 */
export const postWebViewMessage = (type, payload = {}) => {
  if (isWebView()) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    return true;
  }
  return false;
};

/**
 * Open an external HTTP(S) URL without replacing the app WebView.
 *
 * React Native should handle OPEN_EXTERNAL_URL by presenting an in-app browser
 * (SFSafariViewController / Chrome Custom Tabs), which provides a native
 * Done/back control. Normal browsers keep the app open in the current tab.
 */
export const openExternalUrl = (url) => {
  if (!url) return false;

  let destination;
  try {
    destination = new URL(url, window.location.origin);
  } catch {
    return false;
  }

  if (!["http:", "https:"].includes(destination.protocol)) return false;

  if (postWebViewMessage("OPEN_EXTERNAL_URL", { url: destination.href })) {
    return true;
  }

  window.open(destination.href, "_blank", "noopener,noreferrer");
  return true;
};
